import { useMemo, useRef } from 'react'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { TreemapChart, type TreemapSeriesOption } from 'echarts/charts'
import { TooltipComponent, type TooltipComponentOption } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

import type { ComposeOption } from 'echarts/core'
import { ROOT_CATEGORY_NAME, type CategoryTreeNode, type TickerWithValue } from '../../hooks/useCategoriesTree.ts'
import styles from './CategoriesTreeMap.module.css'

echarts.use([TreemapChart, TooltipComponent, CanvasRenderer])

type CategoriesTreeMapOption = ComposeOption<TreemapSeriesOption | TooltipComponentOption>

type TreeMapNode = {
  id?: string
  name: string
  value?: number
  children?: TreeMapNode[]
  itemStyle?: {
    color?: string
    borderColor?: string
  }
  label?: {
    color?: string
  }
  upperLabel?: {
    color?: string
    backgroundColor?: string
  }
  tooltipValueLabel?: string
}

type CategoriesTreeMapProps = {
  // O chart recebe apenas o modelo derivado pronto para apresentação.
  tree: CategoryTreeNode[]
  tickersByCategory: Map<number, TickerWithValue[]>
  leafDepth?: number
}

function formatCurrency(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'

  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

// Paletas manuais por ramo principal. A ideia é:
// - nível 1 usa uma cor forte
// - nível 2 usa tons médios da mesma família
// - folhas repetem a cor herdada do pai para evitar tons claros demais
//
// Aqui é o principal ponto para ajustar manualmente a identidade visual.
// Cada categoria raiz recebe uma dessas famílias pela posição no array.
const ROOT_PALETTES = [
  ['#1e3a8a', '#1d4ed8', '#2563eb', '#5095ea', '#73a5dd'],
  ['#14532d', '#15803d', '#16a34a', '#3ace70', '#86efac'],
  ['#581c87', '#7e22ce', '#a855f7', '#c084fc', '#e9d5ff'],
  ['#7c2d12', '#c2410c', '#ea580c', '#fb923c', '#fdba74'],
  ['#134e4a', '#0f766e', '#14b8a6', '#5eead4', '#99f6e4'],
  ['#78350f', '#b45309', '#f59e0b', '#fbbf24', '#fde68a'],
] as const

function getRootPalette(rootIndex: number) {
  return ROOT_PALETTES[rootIndex % ROOT_PALETTES.length] ?? ROOT_PALETTES[0]
}

function getContrastTextColor(backgroundColor: string) {
  const normalized = backgroundColor.replace('#', '')
  const hex = normalized.length === 3
    ? normalized
        .split('')
        .map((char) => char + char)
        .join('')
    : normalized

  const r = Number.parseInt(hex.slice(0, 2), 16)
  const g = Number.parseInt(hex.slice(2, 4), 16)
  const b = Number.parseInt(hex.slice(4, 6), 16)

  // Fórmula simples de luminância perceptiva para decidir contraste.
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  // Regra mais conservadora: texto branco só em fundos realmente escuros.
  return luminance > 0.5 ? '#111827' : '#ffffff'
}

function buildNodeStyles(color: string) {
  const textColor = getContrastTextColor(color)

  return {
    itemStyle: {
      color,
      borderColor: '#e5e7eb',
    },
    label: {
      color: textColor,
    },
    // Ajustar na configuração global
    /*
    upperLabel: {
      color: textColor,
    },
    */
  }
}

function mapTickersToTreeMap(
  tickers: TickerWithValue[],
  inheritedColor: string,
  parentId: string,
): TreeMapNode[] {
  const styles = buildNodeStyles(inheritedColor)

  return tickers.map((ticker) => ({
    id: `${parentId}/${ticker.ticker}`,
    name: ticker.ticker,
    value: ticker.value ?? 0,
    tooltipValueLabel: formatCurrency(ticker.value),
    ...styles,
  }))
}

function mapCategoryTreeToTreeMap(
  nodes: CategoryTreeNode[],
  tickersByCategory: Map<number, TickerWithValue[]>,
  context?: {
    rootIndex: number
    inheritedColor?: string
    depth?: number
    parentId?: string
  },
): TreeMapNode[] {
  const depth = context?.depth ?? 0
  const shouldSortThisLevel = depth <= 1
  const nodesForMapping = shouldSortThisLevel
    ? [...nodes].sort((a, b) => (b.totalValue ?? 0) - (a.totalValue ?? 0))
    : nodes

  return nodesForMapping.map((node, index) => {
    const depth = context?.depth ?? 0
    const rootIndex = depth === 0 ? index : (context?.rootIndex ?? 0)
    const palette = getRootPalette(rootIndex)
    const nodeId = context?.parentId ? `${context.parentId}/${node.name}` : node.name

    // Regra de cor:
    // - nível 1: cor forte do ramo
    // - nível 2: percorre a paleta inteira do mesmo ramo
    // - nível 3+: repete a cor herdada para preservar contraste
    const color = node.isRoot
      ? '#334155'
      : depth === 0
        ? palette[0]
        : depth === 1
          ? palette[index % palette.length] ?? palette[0]
          : (context?.inheritedColor ?? palette[1] ?? palette[0])

    const styles = buildNodeStyles(color)
    const childCategories = mapCategoryTreeToTreeMap(node.children, tickersByCategory, {
      rootIndex,
      inheritedColor: color,
      depth: node.isRoot ? 0 : depth + 1,
      parentId: nodeId,
    })
    const childTickers = mapTickersToTreeMap(tickersByCategory.get(node.id) ?? [], color, nodeId)
    const children = [...childCategories, ...childTickers]

    return {
      id: nodeId,
      name: node.name,
      value: node.totalValue ?? 0,
      tooltipValueLabel: formatCurrency(node.totalValue),
      children: children.length > 0 ? children : undefined,
      ...styles,
    }
  })
}

export function CategoriesTreeMap({
  tree,
  tickersByCategory,
  leafDepth = 4,
}: CategoriesTreeMapProps) {
  const chartRef = useRef<any>(null)

  const data = useMemo(() => mapCategoryTreeToTreeMap(tree, tickersByCategory), [tree, tickersByCategory])

  const option = useMemo<CategoriesTreeMapOption>(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const treePathInfo = 'treePathInfo' in params && Array.isArray(params.treePathInfo)
            ? params.treePathInfo
            : []
          const path = treePathInfo
            .map((item: { name?: string }) => item.name)
            .filter((name: string | undefined): name is string => Boolean(name) && name !== ROOT_CATEGORY_NAME)
            .join(' / ')

          const nodeData =
            typeof params.data === 'object' && params.data ? (params.data as TreeMapNode) : undefined
          const rawValue =
            typeof params.value === 'number'
              ? params.value
              : Array.isArray(params.value) && typeof params.value[0] === 'number'
                ? params.value[0]
                : null
          const valueLabel = nodeData?.tooltipValueLabel ?? formatCurrency(rawValue)

          return [
            `<div class="${styles.tooltip}">`,
            `<div><strong>${params.name}</strong></div>`,
            path ? `<div>${path}</div>` : '',
            `<div>Valor: ${valueLabel}</div>`,
            '</div>',
          ].join('')
        },
      },
      
      series: [
        {
          type: 'treemap',
          data,
          top: 0,
          bottom: 2,
          left: 0,
          right: 0,
          roam: false,
          
          // O zoom padrão do treemap estava encolhendo e deslocando a área útil.
          // A navegação passa a ser controlada manualmente no handler de click abaixo.
          nodeClick: false,
          //nodeClick: 'zoomToNode',
          leafDepth,

          breadcrumb: {
            show: true,
            itemStyle: {
              color: 'rgba(241, 245, 249, 0.82)',
              borderColor: 'rgba(203, 213, 225, 0.9)',
              borderWidth: 1,
              opacity: 0.6,
            },
            textStyle: {
              color: '#0f172a',
              fontSize: 11,
            },
            height: 18,
            emptyItemWidth: 10,
            bottom: 4,
            //left: 4,
          },
          
          label: {
            show: true,
            formatter: '{b}',
          },
          upperLabel: {
            show: true,
            height: 15,
          },
          itemStyle: {
            //borderColor: '#d1d5db',
            borderWidth: 0,
            gapWidth: 0,
          },

          // As linhas abaixo eram a abordagem automática por escala de valor.
          // Mantidas comentadas para comparação com a abordagem manual por ramo.
          // color: ['#0d3ec8', '#2b72e6', '#93c5fd', '#dbeafe']
          // colorMappingBy: 'value'
          // visualDimension: 0
          levels: [
            {
              itemStyle: {              // nível 1
                borderColor: '#000',
                borderWidth: 0,
                gapWidth: 1,
              },
              upperLabel: {
                backgroundColor: '#333',
                color: '#fff',
              },
            },
            {
              itemStyle: {              // nível 2
                gapWidth: 1,
              },
              upperLabel: {
                backgroundColor: '#444',
                color: '#fff',
              },
            },
            {
              itemStyle: {              // nível 3
                gapWidth: 1,
              },
              upperLabel: {
                backgroundColor: '#555',
                color: '#fff',
              },
            },
            {
              itemStyle: {              // nível 4
                gapWidth: 1,
              },
              upperLabel: {
                backgroundColor: '#666',
                color: '#fff',
              },
            },
          ],

        },
      ],
      media: [
        {
          query: {
            maxWidth: 639,
          },
          option: {
            series: [
              {
                label: {
                  show: true,
                  formatter: '{b}',
                  fontSize: 9,
                },
                upperLabel: {
                  show: true,
                  height: 14,
                  fontSize: 10,
                },
              },
            ],
          },
        },
      ],
    }),
    [data, leafDepth],
  )

  const onEvents = useMemo(
    () => ({
      click: (params: any) => {
        if (params.seriesType !== 'treemap') return

        const chart = chartRef.current?.getEchartsInstance()
        const targetNodeId = params.data?.id

        if (!chart || !targetNodeId) return

        chart.dispatchAction({
          type: 'treemapRootToNode',
          seriesIndex: 0,
          targetNodeId,
        })
      },
    }),
    [],
  )

  return (
    <div className={styles.chartShell}>
      <ReactEChartsCore
        ref={chartRef}
        echarts={echarts}
        option={option}
        onEvents={onEvents}
        notMerge
        lazyUpdate
        className={styles.chart}
      />
    </div>
  )
}
